<template>
    <div class="company-picker">
        <div class="header">
            <div class="header__content">
                <div class="header__top">
                    <img class="header__top__logo" :src="$whitelabel.resources.logo" alt="logo"/>
                    <div class="header__top__logout">
                        <b>
                            {{ username }}
                        </b>
                        <div
                            class="ot-label ot-clickable"
                            :title="logoutText"
                            role="button"
                            tabindex="0"
                            @click="$emit('logout')"
                            @keydown.space="$emit('logout')"
                            @keydown.enter="$emit('logout')"
                        >
                            {{ $t('login.company_picker.logout.text') }}
                        </div>
                    </div>
                </div>
                <h2>
                    <slot name="title">
                        {{ $t('login.company_picker.authorize', { whitelabel: $whitelabel.name, clientName }) }}
                    </slot>
                </h2>
            </div>
        </div>
        <OtCardContent>
            <div v-if="companyOptions">
                <OtInputField>
                    <OtInput
                        type="select"
                        v-model="picked_companies"
                        :options='company_options'
                        :multiple="maxCompanies !== 1"
                        :max="maxCompanies === 0 ? null : maxCompanies"
                        @search-change="searchChange"
                        :disabled="all_companies"
                        searchable
                    />
                </OtInputField>
                <OtInputField v-if="maxCompanies === 0">
                    <OtInput
                        type="checkbox"
                        v-model="all_companies"
                        :label="$t('login.company_picker.select_all.label')"
                    />
                </OtInputField>
                <div class="footer">
                    <OtButton
                        :disabled="picked_companies.length === 0 && !all_companies"
                        class="ot-button is-fullwidth"
                        @click="pick"
                        :title="$t('login.company_picker.give_access.title')"
                    >
                        <i class="oti oti-check ot-button-icon--left"/>
                        {{ $t('login.company_picker.give_access.text') }}
                    </OtButton>
                </div>
            </div>
            <OtAside
                v-else
                icon="oti-warning"
                type="is-danger"
            >
                <template #description>
                    {{ $t('login.company_picker.companies_select.warning') }}
                </template>
            </OtAside>
        </OtCardContent>
    </div>
</template>

<script lang="ts">

import { Component, Prop, Vue } from 'vue-property-decorator';
import Axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import urlJoin from 'url-join';
import OtButton from './OtButton.vue';

@Component({ components: { OtButton } })
export default class CompanyPicker extends Vue {

    @Prop({ required: true }) companies!: [ { guid: string, name: string } ];
    @Prop({ required: true }) username!: string;
    @Prop({ default: null }) maxCompanies!: number | null;
    @Prop({ default: null }) error!: string | null;
    @Prop({ required: true }) clientName!: string;

    companyOptions: [ { guid: string, name: string } ] | null = null;

    picked_companies: string[] | null = [];
    all_companies: boolean | null = null;

    search: string | null = null;

    http: AxiosInstance = Axios.create();

    created(): void {
        this.companyOptions = this.companies;
        this.http.defaults.withCredentials = true;
    }

    async searchChange(search: string): Promise<void> {
        if (!search) {
            return;
        }

        this.search = search;

        await this.http.get(urlJoin(this.$whitelabel.login.auth_api_url, 'tokens', 'companies', `?like=${this.search}`))
            .then((response: AxiosResponse<[ { guid: string, name: string } ]>) => {
                this.companyOptions = response.data;
            });
    }

    get company_options(): { [key: string]: string } {
        if (!this.companyOptions) {
            return {};
        }

        const res: { [key: string]: string } = {};
        for (const company of this.companyOptions) {
            res[company.guid] = company.name;
        }
        return res;
    }

    get logoutText(): string {
        return this.$t('login.company_picker.logout.text') as string;
    }

    pick(): void {
        const _picked = this.all_companies ? '*' : this.picked_companies;
        this.$emit('pick', new Array(0).concat(_picked));
    }

}
</script>

<style lang="scss" scoped>
.header {
    &__content {
        flex: 1;
        padding: var(--ot-spacing-2xl) var(--ot-spacing-4xl);
    }

    &__top {
        display: flex;
        justify-content: space-between;

        &__logo {
            max-width: 40%;
        }

        &__logout{
            text-align: right;
            display: inline-grid;
        }
    }

    h2 {
        padding-top: var(--ot-spacing-xl);
    }
}

.footer {
    display: flex;
    margin-top: var(--ot-spacing-2xl);

    .ot-button {
        margin: 0;
    }
}
</style>
